import {Component, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {TreeNode} from 'primeng/api';
import {Dictionary} from 'lodash';
import {Tree} from 'primeng/tree';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {CpmCRouteResourceApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-route-resource.api-service';
import {CpmCUserRoleApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-user-role.api-model';
import {CpmCUserRoleApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {CpmCRouteResourceApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-route-resource.api-model';
import {ECpmCUserRoleTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {StringUtils} from '../../../../sdk/src/utils/string.util';
import {
    CpmCSdkUserRoleCreateApiSchema,
    CpmCSdkUserRoleUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/cpmc-sdk-user-role.api-schemas';
import {UserRoleUtils} from '../../utils/user-role.utils';

export class NewEditUserRoleModalConfig {
    constructor(
        public existing?: CpmCUserRoleApiModel,
        public fromGlobalView?: boolean
    ) {}
}
export class NewEditUserRoleModalResult {
    constructor(public template?: CpmCUserRoleApiModel) {}
}
/**
 * New/Edit UserRole modal
 */
@Component({
    selector: 'app-new-edit-user-role-modal',
    templateUrl: './new-edit-user-role-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditUserRoleModalComponent extends IBaseCRUModalComponent<
    CpmCUserRoleApiModel,
    NewEditUserRoleModalComponent,
    NewEditUserRoleModalConfig,
    NewEditUserRoleModalResult
> {
    private lastUserRoleType?: ECpmCUserRoleTypeApiEnum;

    protected item!: CpmCUserRoleApiModel;
    protected userRoleTypeOptions = UserRoleUtils.typeOptions;
    protected treeLoading = true;

    protected routeResources: CpmCRouteResourceApiModel[] = [];
    protected routeResourceTreeNodes: TreeNode<CpmCRouteResourceApiModel>[] = [];
    protected routeResourcesTreeNodeByUUID: Dictionary<TreeNode<CpmCRouteResourceApiModel>> = {};
    protected routeResourceSelectedTreeNodes: TreeNode<CpmCRouteResourceApiModel>[] = [];

    @ViewChild(Tree, {static: true})
    protected resourcesTree!: Tree;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('UserRoleApiModel', (model: CpmCUserRoleApiModel) => model.name);
        this.config.width = '85rem';
        this.contentMinHeight = '35rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            type: new FormControl<string | null>(null, [Validators.required]),
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, [Validators.required])
        });

        if (this.config.data?.extended?.fromGlobalView === false) {
            this.userRoleTypeOptions = UserRoleUtils.typeOptions.filter(
                (x) => x.value == ECpmCUserRoleTypeApiEnum.MULTI_SCHEMA_TENANT_ROLE
            );
        }

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the user role...';
            this.item = await firstValueFrom(
                CpmCUserRoleApiService.getUserRoleByUUID(this.config.data?.extended?.existing?.uuid ?? '')
            );

            this.form.patchValue(this.item);
            this.form.controls['type'].disable();
        } else {
            this.form.controls['type'].setValue(ECpmCUserRoleTypeApiEnum.MULTI_SCHEMA_TENANT_ROLE);
        }

        await this.onTypeChanged();

        this.routeResourceSelectedTreeNodes =
            this.item?.route_resources
                ?.map((x) => this.routeResourcesTreeNodeByUUID[x.uuid])
                ?.filter((x) => x !== undefined) ?? [];
        this.updateResourcesSelectionState();

        // Update the form when the type change
        this.form.controls['type'].valueChanges.subscribe(this.onTypeChanged.bind(this));
        // Recompute the selected/partially selected/unselected state on filter change
        this.resourcesTree.onFilter.subscribe(() => this.updateResourcesSelectionState());
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        const routeResourcesUUIDs = this.routeResourceSelectedTreeNodes
            .filter((x) => x.data != undefined)
            .map((x) => x.data!.uuid);

        const toSubmit: CpmCSdkUserRoleCreateApiSchema | CpmCSdkUserRoleUpdateApiSchema = this.isCreationMode
            ? {
                  type: this.form.controls['type'].value,
                  name: this.form.controls['name']?.value,
                  description: this.form.controls['description']?.value,
                  route_resources: routeResourcesUUIDs
              }
            : {
                  name: this.form.controls['name']?.value,
                  description: this.form.controls['description']?.value,
                  route_resources: routeResourcesUUIDs
              };

        await this.submitCU(
            null,
            CpmCUserRoleApiService.createUserRole,
            CpmCUserRoleApiService.updateUserRole,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCUserRoleApiModel) => new NewEditUserRoleModalResult(resultCRUD)
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the User Role type changed
     */
    protected async onTypeChanged() {
        console.log('onTypeChanged');
        if (this.form.controls['type'].value === this.lastUserRoleType) return;

        this.treeLoading = true;
        this.lastUserRoleType = this.form.controls['type'].value;

        if (!this.form.controls['type'].value || this.form.controls['type'].value == null) {
            return;
        }
        // Loading resources
        this.loadingStr = 'Loading route resources...';
        this.routeResources = (
            await firstValueFrom(
                CpmCRouteResourceApiService.getRouteResourceList(
                    {
                        for_user_role_type: this.form.controls['type'].value
                    },
                    CpmCRouteResourceApiService.dPaginatorConfig
                )
            )
        ).data;

        this.updateRouteResourcesOptions();

        this.treeLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Update route resources options
     */
    private updateRouteResourcesOptions() {
        this.routeResourceSelectedTreeNodes = [];
        this.routeResourceTreeNodes = [];

        // We want to make sure global is first
        const globalBranch = this.getOrCreateRouteResourceLastBranch(['GLOBAL']);

        for (const current of this.routeResources) {
            const scopeParts = current.scope.split('__');
            const lastParent = this.getOrCreateRouteResourceLastBranch(scopeParts);
            const newLeaf: TreeNode<CpmCRouteResourceApiModel> = {
                key: current.uuid,
                label: `${current.name} [${StringUtils.toHumanFormatedStr(current.type)}]`,
                data: current,
                parent: parent,
                children: [],
                selectable: this.dialogOpenMode !== EDialogOpenMode.READ_ONLY && current.assignable
            };

            lastParent!.children?.push(newLeaf);

            this.routeResourcesTreeNodeByUUID[current.uuid!] = newLeaf;
        }

        if (globalBranch?.children?.length === 0) this.routeResourceTreeNodes.splice(0, 1);
        if (this.routeResourceTreeNodes.length === 1) this.routeResourceTreeNodes[0].expanded = true;
    }
    /**
     * Get and/or create up to the last branch for a scope path
     * @param scopeParts Scope path parts
     * @param scopePartI Current index
     * @param currentNode Current lookup node (for recursion)
     * @returns Create or found last branch
     */
    private getOrCreateRouteResourceLastBranch(
        scopeParts: string[],
        scopePartI = 0,
        currentNode?: TreeNode
    ): TreeNode<CpmCRouteResourceApiModel> | undefined {
        if (scopePartI == scopeParts.length) {
            return currentNode;
        }

        const targetBranch = currentNode?.children ?? this.routeResourceTreeNodes;

        const prefix = currentNode ? currentNode.key + '__' : '';
        const result = TreeUtils.findTreeNodeByKey(targetBranch, prefix + scopeParts[scopePartI]);
        if (result) return this.getOrCreateRouteResourceLastBranch(scopeParts, scopePartI + 1, result);

        const newBranch: TreeNode<CpmCRouteResourceApiModel> = {
            key: prefix + scopeParts[scopePartI],
            label: StringUtils.toHumanFormatedStr(scopeParts[scopePartI]),
            children: [],
            parent: currentNode,
            selectable: this.dialogOpenMode !== EDialogOpenMode.READ_ONLY
        };
        targetBranch.push(newBranch);

        return this.getOrCreateRouteResourceLastBranch(scopeParts, scopePartI + 1, newBranch);
    }
    /**
     * Update route resources selection state
     */
    protected updateResourcesSelectionState() {
        this.routeResourceTreeNodes.forEach((x) =>
            TreeUtils.updateTreeNodeSelectionState(x, this.routeResourceSelectedTreeNodes)
        );
    }
}
